/* eslint-disable camelcase */
// Pure helpers for the Gift cards section on the student profile (PROD-9929).

import moment from 'moment-timezone';
import { day_format_year } from '../../../../constants/index';

const GENERIC_ERROR = 'Could not load gift cards. Please try again.';

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  sent: 'Sent',
  used: 'Used',
  'partly used': 'Partly used'
};

const has_code = (row) => typeof row?.gift_code === 'string' && row.gift_code !== '';

// Self when in both lists, marked self by apis, or carrying a code (D-45).
export const gift_card_direction = (row, in_sent, in_received) => {
  if ((in_sent && in_received) || row?.direction === 'self' || has_code(row)) return 'Self';
  return in_sent ? 'Sent' : 'Received';
};

export const gift_card_status_label = (status) => {
  if (status === null || status === undefined || status === '') return '—';
  return STATUS_LABELS[status] || String(status);
};

// Gift amounts are major units; every gift currency has 2 decimals.
export const format_gift_amount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  const code = typeof currency === 'string' ? currency.trim().toUpperCase() : '';
  return code ? `${value.toFixed(2)} ${code}` : value.toFixed(2);
};

// Send date is a calendar date: read it in UTC so no time-zone shift moves the day.
export const format_send_date = (value) => {
  if (!value) return '—';
  const date = moment.utc(value);
  return date.isValid() ? date.format(day_format_year) : '—';
};

export const gift_cards_error_message = (error) => {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : GENERIC_ERROR;
};

const created_time = (row) => {
  const time = Date.parse(row.created_date);
  return Number.isNaN(time) ? -Infinity : time;
};

// Merges sent and received into one list, one row per uuid, newest first.
export const build_gift_card_rows = (data) => {
  const sent = Array.isArray(data?.sent) ? data.sent : [];
  const received = Array.isArray(data?.received) ? data.received : [];
  const by_key = new Map();
  const add = (list, side) => {
    list.forEach((row, index) => {
      if (!row || typeof row !== 'object') return;
      const key = row.uuid ? String(row.uuid) : `${side}-${index}`;
      const entry = by_key.get(key) || { row, in_sent: false, in_received: false };
      entry[side] = true;
      by_key.set(key, entry);
    });
  };
  add(sent, 'in_sent');
  add(received, 'in_received');

  return [...by_key.entries()]
    .map(([key, { row, in_sent, in_received }]) => {
      const direction = gift_card_direction(row, in_sent, in_received);
      const is_self = direction === 'Self';
      return {
        key,
        direction,
        other_party: is_self ? 'Self' : row.other_party_email || '—',
        status_label: gift_card_status_label(row.status),
        message_date: row.message_date || null,
        created_date: row.created_date || null,
        amount_text: format_gift_amount(row.amount, row.currency),
        amount_left_text: format_gift_amount(row.amount_left, row.currency),
        gift_code: is_self && has_code(row) ? row.gift_code : ''
      };
    })
    .sort((a, b) => created_time(b) - created_time(a));
};

// A 2xx body counts only when it is a success with both lists.
export const is_gift_cards_body = (body) =>
  body?.status === 'success' &&
  Array.isArray(body?.data?.sent) &&
  Array.isArray(body?.data?.received);
